import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  DrinkListResponseSchema,
  DrinkSchema,
  type CreateDrinkInput,
  type Drink,
  type DrinkStatus,
  type PatchDrinkInput,
  type SpiritBase,
} from "@/api/types/drink";

/** Query-key factory — mirrors the events/quotes hook convention. */
export const drinkKeys = {
  all: ["drinks"] as const,
  lists: () => [...drinkKeys.all, "list"] as const,
  list: (params: { status?: DrinkStatus; spirit_base?: SpiritBase; q?: string; limit?: number }) =>
    [...drinkKeys.lists(), params] as const,
  details: () => [...drinkKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...drinkKeys.details(), id] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: drinkKeys.all });
}

interface UseDrinksParams {
  status?: DrinkStatus;
  spirit_base?: SpiritBase;
  q?: string;
  limit?: number;
}

/** List query with cursor pagination, mirroring useContacts'/useEvents' convention. */
export function useDrinks(params: UseDrinksParams = {}) {
  const { status, spirit_base, q, limit = 50 } = params;

  return useInfiniteQuery({
    queryKey: drinkKeys.list({ status, spirit_base, q, limit }),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (status) searchParams.set("status", status);
      if (spirit_base) searchParams.set("spirit_base", spirit_base);
      if (q?.trim()) searchParams.set("q", q.trim());
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(`/v2/drinks?${searchParams.toString()}`);
      return validateResponse(DrinkListResponseSchema, data, "GET /v2/drinks");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30 * 1000,
  });
}

export function useDrink(drinkId: string | undefined) {
  return useQuery<Drink>({
    queryKey: drinkKeys.detail(drinkId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/drinks/${drinkId}`);
      return validateResponse(DrinkSchema, data, "GET /v2/drinks/:id");
    },
    enabled: !!drinkId,
  });
}

export function useCreateDrink() {
  const queryClient = useQueryClient();
  return useMutation<Drink, unknown, CreateDrinkInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/drinks", input);
      return validateResponse(DrinkSchema, data, "POST /v2/drinks");
    },
    onSuccess: (drink) => {
      queryClient.setQueryData(drinkKeys.detail(drink.id), drink);
      invalidateAll(queryClient);
    },
  });
}

export function usePatchDrink() {
  const queryClient = useQueryClient();
  return useMutation<Drink, unknown, PatchDrinkInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/drinks/${id}`, patch);
      return validateResponse(DrinkSchema, data, "PATCH /v2/drinks/:id");
    },
    onSuccess: (drink) => {
      queryClient.setQueryData(drinkKeys.detail(drink.id), drink);
      invalidateAll(queryClient);
    },
  });
}

export function useDeleteDrink() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/v2/drinks/${id}`);
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
}
